import React, { useCallback } from "react";
import classes from "./integromat-result-box.module.scss";
import copy from "copy-to-clipboard";
import { notification } from "antd";
import classNames from "classnames";
import { AnnotationObject } from "../../../../../object-templates/annotation";
import { isEmpty, isNil, pluck } from "ramda";
import { ImageObject } from "../../../../../object-templates/image";
import { FieldObject } from "../../../../../object-templates/field";
import { OBJECT_TYPES } from "../../../../../constants/project";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveDetectedFieldId,
  selectActiveObjectId,
} from "../../../../../reducers/editor/selectors";
import { setPdfPage } from "../../../../../reducers/pdf-viewer/actions";
import {
  setActiveDetectedField,
  setActiveObjectId,
} from "../../../../../reducers/editor/actions";
import {
  selectPage,
  selectTotalPages,
} from "../../../../../reducers/pdf-viewer/selectors";
import TextArea from "antd/es/input/TextArea";
import {renameKeys, transformValueForZapierAndMake} from "../../../../../helpers/common";

const IntegromatResultBox = ({ data }) => {
  const dispatch = useDispatch();
  const activeObjectId = useSelector(selectActiveObjectId);
  const activeDetectedFieldId = useSelector(selectActiveDetectedFieldId);
  const totalPages = useSelector(selectTotalPages);
  const currentPage = useSelector(selectPage);

  const annotationParams = pluck("name", AnnotationObject.attributes);
  const imageParams = pluck("name", ImageObject.attributes);
  const fieldParams = pluck("name", FieldObject.attributes);

  const annotationData = data
    .filter((item) => item.objectType === OBJECT_TYPES.ANNOTATION)
    .map((annotation) => {
      let data = {
        ...annotation,
        showData: {},
      };
      annotationParams.map((param) => {
        if (!isNil(annotation[param]) && !isEmpty(annotation[param])) {
          if (param === "text") {
            data.showData[param] = transformValueForZapierAndMake(annotation[param]);
          } else {
            data.showData[param] = annotation[param];
          }
        }
      });

      return data;
    });

  const imageData = data
    .filter((item) => item.objectType === OBJECT_TYPES.IMAGE)
    .map((image) => {
      let data = {
        ...image,
        showData: {},
      };
      imageParams.map((param) => {
        if (!isNil(image[param]) && !isEmpty(image[param])) {
          data.showData[param] = image[param];
        }
      });

      return data;
    });

  const fieldData = data
    .filter((item) => item.objectType === OBJECT_TYPES.DETECTED_FIELD)
    .map((field) => {
      const showData = {};
      fieldParams.map((param) => {
        if (!isNil(field[param]) && !isEmpty(field[param])) {
          if (param === "text") {
            showData[param] = transformValueForZapierAndMake(field[param]);
          } else {
            showData[param] = field[param];
          }
        }
      });

      return {
        ...field,
        showData: renameKeys(
          {
            altFieldName: "fieldName",
          },
          showData
        ),
      };
    });

  const onCopyItem = (item) => {
    copy(item);
    notification.success({
      message: "Copied!",
    });
  };

  const onClickItem = (item) => {
    const page = Number(item.pages) + 1;
    if (page !== currentPage && page <= totalPages) {
      dispatch(setPdfPage(page));
    }
    if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
      dispatch(setActiveDetectedField(item.objectId ? item.objectId : item.id));
    } else {
      dispatch(setActiveObjectId(item.objectId));
    }
  };

  const isActive = useCallback(
    (item) => {
      if (item.objectType === OBJECT_TYPES.DETECTED_FIELD) {
        return item.objectId
          ? activeDetectedFieldId === item.objectId
          : activeDetectedFieldId === item.id;
      }
      return activeObjectId === item.objectId;
    },
    [activeObjectId, activeDetectedFieldId]
  );

  const ShowItem = ({ item, key }) => {
    return (
      <div
        key={key}
        className={classNames(classes.ListItem, {
          [classes.ActiveObject]: isActive(item),
        })}
        onClick={() => onClickItem(item)}
      >
        {Object.keys(item.showData).map((key, index) => (
          <div key={index} className={classes.SingleItem}>
            <div className={classes.SingleItemValue}>
              <span className="me-2 fw-bolder">{key}:</span>
              {item.showData[key].toString()}
            </div>
            <div className={classes.SingleItemCopy}>
              <button
                className={classNames(classes.CopyItemIcon, "btn btn-primary")}
                onClick={() => onCopyItem(item.showData[key])}
              >
                Copy
              </button>
              {/*<Tooltip title="Copy">*/}
              {/*  <FontAwesomeIcon*/}
              {/*    className={classNames(classes.CopyItemIcon, "text-primary")}*/}
              {/*    icon={faCopy}*/}
              {/*    onClick={() => onCopyItem(item.showData[key])}*/}
              {/*  />*/}
              {/*</Tooltip>*/}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={classes.BoxWrap}>
      {data ? (
        <div className={classNames(classes.ListWrap)}>
          {!!annotationData.length && (
            <>
              <div className="ms-1 fw-bold text-primary mb-1">Text objects</div>
              {annotationData.map((item, index) => (
                <ShowItem item={item} key={index} />
              ))}
            </>
          )}

          {!!imageData.length && (
            <>
              <div className="ms-1 fw-bold text-primary mb-1">
                Image objects
              </div>
              {imageData.map((item, index) => (
                <ShowItem item={item} key={index} />
              ))}
            </>
          )}

          {!!fieldData.length && (
            <>
              <div className="ms-1 fw-bold text-primary mb-1">
                Field objects
              </div>
              {fieldData.map((item, index) => (
                <ShowItem item={item} key={index} />
              ))}
            </>
          )}
        </div>
      ) : (
        <TextArea value={data} className={classes.TextArea} />
      )}
    </div>
  );
};

export default IntegromatResultBox;
